import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

/**
 * Drag-to-reveal before/after image comparison.
 * Drag the gold handle left/right (mouse or touch) to wipe between the two images.
 */
export default function BeforeAfter({ beforeSrc, afterSrc, label = "", testid = "before-after" }) {
    const [pos, setPos] = useState(50); // percentage
    const containerRef = useRef(null);
    const draggingRef = useRef(false);

    const updateFromClientX = (clientX) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = clientX - rect.left;
        const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
        setPos(pct);
    };

    const onMouseDown = (e) => { draggingRef.current = true; updateFromClientX(e.clientX); };
    const onMouseMove = (e) => { if (draggingRef.current) updateFromClientX(e.clientX); };
    const onUp = () => { draggingRef.current = false; };
    const onTouchStart = (e) => { draggingRef.current = true; updateFromClientX(e.touches[0].clientX); };
    const onTouchMove = (e) => { if (draggingRef.current) updateFromClientX(e.touches[0].clientX); };

    return (
        <div
            ref={containerRef}
            data-testid={testid}
            className="relative aspect-[4/3] w-full overflow-hidden border border-gold/30 select-none cursor-ew-resize touch-none bg-navy"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onUp}
        >
            {/* BEFORE (full) */}
            <img src={beforeSrc} alt="Before cleaning" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <span className="absolute top-4 left-4 bg-navy/80 text-cream text-[10px] uppercase tracking-[0.3em] px-3 py-1 border border-cream/30 font-outfit">Before</span>

            {/* AFTER (clipped) */}
            <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
                <img src={afterSrc} alt="After cleaning" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                <span className="absolute top-4 right-4 bg-gold text-navy text-[10px] uppercase tracking-[0.3em] px-3 py-1 font-outfit font-semibold">After</span>
            </div>

            {/* Slider line + handle */}
            <div className="absolute top-0 bottom-0 w-px bg-gold pointer-events-none" style={{ left: `${pos}%` }}>
                <motion.div
                    whileHover={{ scale: 1.12 }}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-gold border-2 border-white shadow-[0_0_24px_rgba(212,175,55,0.6)] flex items-center justify-center pointer-events-auto"
                >
                    <MoveHorizontal className="w-5 h-5 text-navy" />
                </motion.div>
            </div>

            {label && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-navy/80 text-cream text-xs font-outfit uppercase tracking-[0.2em] px-4 py-1.5 border border-gold/40">
                    {label}
                </div>
            )}
        </div>
    );
}
